// The learning base, the owner's side (docs/specs/LEARNING_BASE.md; migration 20270306100000; act group "learning"):
// What's working (crm_whats_working, for the business or one campaign), lessons kept, paused or rejected
// (set_lesson_status), "Make this a rule" from a draft note (add_instruction), a campaign's brief read into chips and
// saved (parse_brief, set_campaign_brief), and "Why this draft" from a draft's ledger (outbox.metadata.ledger).
// Everything read here is read tolerantly, so a missing field never breaks a page.
import type { Crm } from "./crm";
import { plural } from "./format";

export type Confidence = "early" | "likely" | "proven";
export type LessonKind = "instruction" | "performance" | "reply_insight" | "warning";
export type LessonStatus = "candidate" | "active" | "pinned" | "retired" | "vetoed";
export type StepKind = "first" | "follow_up" | "close";
export type ResultSource = "campaign" | "business" | "sector";

export interface Feature {
  feature: string;
  value: string;
  label: string;
  step_kind: StepKind;
  sends: number;
  positives: number;
  rate: number | null;
  lift: number | null;
  confidence: Confidence;
  source: ResultSource;
}

export interface Lesson {
  id: string;
  kind: LessonKind;
  statement: string;
  scope: "business" | "campaign";
  campaign_id: string | null;
  status: LessonStatus;
  confidence: Confidence | null;
  source: string;
  evidence: { sends?: number; positives?: number; rate?: number; lift?: number };
  created_at: string | null;
  new: boolean;
}

export interface WhatsWorking {
  totals: { sent: number; positive: number; rate: number | null; since: string | null };
  explore_share: number;
  features: Feature[];
  lessons: Lesson[];
  testing: { feature: string; value: string; label: string; sends: number }[];
  sector: { key: string; label: string; pooled: boolean; sharing: boolean } | null;
}

export interface BriefChips {
  lead_with: string[];
  mention: string[];
  never: string[];
  cta: string | null;
}

/** plays.brief: what the owner told Ogma this campaign should say, and the chips they confirmed. */
export interface CampaignBrief {
  text: string;
  chips: BriefChips;
}

/** What a draft's ledger says about why it was written as it was (ledger.strategy and ledger.provenance). */
export interface DraftWhy {
  hook: string | null;
  why: string | null;
  lead_with: string | null;
  lessons: { id: string; kind: string; statement: string }[];
  instructions: string[];
  play_id: string | null;
}

// ---------------------------------------------------------------- tolerant readers
type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj => (v && typeof v === "object" && !Array.isArray(v) ? (v as Obj) : {});
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x.trim() !== "") : []);
const oneOf = <T extends string>(v: unknown, list: readonly T[], fallback: T): T => (list.find((x) => x === v) ?? fallback);

const CONFIDENCES = ["early", "likely", "proven"] as const;
const KINDS = ["instruction", "performance", "reply_insight", "warning"] as const;
const STATUSES = ["candidate", "active", "pinned", "retired", "vetoed"] as const;

function readLesson(v: unknown): Lesson | null {
  const o = obj(v);
  const id = str(o.id), statement = str(o.statement);
  if (!id || !statement) return null;
  const e = obj(o.evidence);
  return {
    id, statement, kind: oneOf(o.kind, KINDS, "performance"), scope: o.scope === "campaign" ? "campaign" : "business",
    campaign_id: str(o.campaign_id), status: oneOf(o.status, STATUSES, "candidate"),
    confidence: CONFIDENCES.find((c) => c === o.confidence) ?? null, source: str(o.source) ?? "results",
    evidence: { sends: num(e.sends) ?? undefined, positives: num(e.positives) ?? undefined, rate: num(e.rate) ?? undefined, lift: num(e.lift) ?? undefined },
    created_at: str(o.created_at), new: o.new === true,
  };
}

export function readWhatsWorking(v: unknown): WhatsWorking {
  const o = obj(v);
  const t = obj(o.totals);
  const s = obj(o.sector);
  return {
    totals: { sent: num(t.sent) ?? 0, positive: num(t.positive) ?? 0, rate: num(t.rate), since: str(t.since) },
    explore_share: num(o.explore_share) ?? 0,
    features: (Array.isArray(o.features) ? o.features : []).map(obj).filter((f) => str(f.feature) && str(f.value)).map((f) => ({
      feature: String(f.feature), value: String(f.value), label: str(f.label) ?? String(f.value),
      step_kind: oneOf(f.step_kind, ["first", "follow_up", "close"] as const, "first"),
      sends: num(f.sends) ?? 0, positives: num(f.positives) ?? 0, rate: num(f.rate), lift: num(f.lift),
      confidence: oneOf(f.confidence, CONFIDENCES, "early"), source: oneOf(f.source, ["campaign", "business", "sector"] as const, "business"),
    })),
    lessons: (Array.isArray(o.lessons) ? o.lessons : []).map(readLesson).filter((l): l is Lesson => l !== null),
    testing: (Array.isArray(o.testing) ? o.testing : []).map(obj).filter((x) => str(x.value)).map((x) => ({
      feature: String(x.feature ?? ""), value: String(x.value), label: str(x.label) ?? String(x.value), sends: num(x.sends) ?? 0,
    })),
    sector: str(s.key) ? { key: String(s.key), label: str(s.label) ?? String(s.key), pooled: s.pooled === true, sharing: s.sharing !== false } : null,
  };
}

export function readChips(v: unknown): BriefChips {
  const o = obj(v);
  return { lead_with: strs(o.lead_with), mention: strs(o.mention), never: strs(o.never), cta: str(o.cta) };
}

export function readBrief(v: unknown): CampaignBrief | null {
  const o = obj(v);
  const text = str(o.text);
  return text ? { text, chips: readChips(o.chips) } : null;
}

/** "Why this draft" from outbox.metadata.ledger; null when the draft has no ledger (older drafts, by-hand messages). */
export function readWhy(ledger: unknown): DraftWhy | null {
  const l = obj(ledger);
  if (!Object.keys(l).length) return null;
  const p = obj(l.provenance);
  const choice = obj(p.choice);
  return {
    hook: str(choice.hook) ?? str(obj(l.strategy).hook_type),
    why: str(choice.why),
    lead_with: str(choice.lead_with),
    lessons: (Array.isArray(p.lessons) ? p.lessons : []).map(obj).filter((x) => str(x.statement))
      .map((x) => ({ id: String(x.id ?? ""), kind: String(x.kind ?? ""), statement: String(x.statement) })),
    instructions: strs(p.instructions),
    play_id: str(obj(l.context).play_id),
  };
}

// ---------------------------------------------------------------- words
/** engine/data/strategy_taxonomy.yaml hook_types (learning.test.ts checks they match). */
export const HOOK_LABELS: Record<string, string> = {
  reason: "A reason Find picked (a signal pack's reason)",
  hiring: "A job they are advertising",
  fact: "A public fact about the firm",
  stored_hook: "The firm's stored opening line",
  segment_only: "No personal claim (who they are, in general)",
};

/** What each kind of result is about, in a word or two. */
export const FEATURE_NAMES: Record<string, string> = {
  hook_type: "Opening", fact_category: "Fact used", cta_type: "Ask", proof_type: "Proof", subject_style: "Subject",
  length_band: "Length", personalisation: "Personal touch", angle: "Angle", signal: "Signal", offer: "Offer",
};

export const CONFIDENCE_WORDS: Record<Confidence, string> = { early: "Early signs", likely: "Likely", proven: "Proven" };
export const STEP_WORDS: Record<StepKind, string> = { first: "First emails", follow_up: "Follow-ups", close: "Last emails" };

/** "Your results" or "Firms like yours" (pooled counts from the sector). */
export const sourceWords = (s: ResultSource | string) => (s === "sector" ? "Firms like yours" : "Your results");

const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const hookWords = (hook: string | null) => lower((hook && HOOK_LABELS[hook]) || "a detail about the firm").replace(/ \(.*\)$/, "");

/** The draft's choice and its reason, in plain words. */
export function whyText(w: DraftWhy): string {
  const hook = hookWords(w.hook);
  switch (w.why) {
    case "best_evidence": return `Leads with ${hook}: best results so far`;
    case "testing": return `Testing: ${hook} (some first emails try something new, so Ogma keeps learning)`;
    case "your_instruction": return `Your instruction: ${w.lead_with ?? w.instructions[0] ?? "lead with what you asked"}`;
    case "find_reason": return "Leads with the reason Find picked this firm";
    case "no_facts": return "No detail about this firm to use, so it speaks to firms like theirs";
    default: return `Leads with ${hook}: no results to go on yet`;
  }
}

export const percent = (r: number | null) => (r === null ? "–" : `${Math.round(r * 1000) / 10}%`);
export const liftText = (lift: number | null) => (lift === null ? "" : `${(Math.round(lift * 10) / 10).toFixed(1)}× average`);

/** "1 in 5": how often a first email tries a less-proven option. */
export function exploreWords(share: number): string | null {
  if (!(share > 0)) return null;
  return `1 in ${Math.max(2, Math.round(1 / share))}`;
}

/** The brief's chips as one line: "Lead with … · Mention … · Never … · Ask for …". */
export function chipsLine(c: BriefChips): string {
  return [
    c.lead_with.length ? `Lead with ${c.lead_with.join(", ")}` : null,
    c.mention.length ? `Mention ${c.mention.join(", ")}` : null,
    c.never.length ? `Never ${c.never.join(", ")}` : null,
    c.cta ? `Ask for ${c.cta}` : null,
  ].filter(Boolean).join(" · ");
}

export const sentWords = (n: number) => `${n.toLocaleString("en-GB")} ${plural(n, "email")} sent`;

// ---------------------------------------------------------------- what the page shows
const strong = (f: Feature) => f.confidence !== "early" && f.lift !== null;

/** Working best: likely or proven, above average, best first. */
export const workingBest = (d: WhatsWorking, n = 5) => d.features.filter((f) => strong(f) && f.lift! > 1).sort((a, b) => b.lift! - a.lift!).slice(0, n);
/** Not working: likely or proven, below average, worst first. */
export const notWorking = (d: WhatsWorking, n = 5) => d.features.filter((f) => strong(f) && f.lift! < 1).sort((a, b) => a.lift! - b.lift!).slice(0, n);
/** Lessons Ogma uses now, and lessons from replies waiting for a yes. */
export const lessonsInUse = (d: WhatsWorking) => d.lessons.filter((l) => l.status === "active" || l.status === "pinned" || (l.kind === "reply_insight" && l.status === "candidate"));

/** The buttons a lesson offers, by its kind and status. */
export function lessonActions(l: Lesson): { status: Exclude<LessonStatus, "candidate">; label: string }[] {
  if (l.kind === "instruction") return [{ status: "retired", label: "Retire" }];
  if (l.kind === "reply_insight" && l.status === "candidate") return [{ status: "active", label: "Use" }, { status: "retired", label: "Dismiss" }];
  return [l.status === "pinned" ? { status: "active", label: "Unpin" } : { status: "pinned", label: "Pin" }, { status: "vetoed", label: "Veto" }];
}

// ---------------------------------------------------------------- calls
export const whatsWorking = async (crm: Crm, campaign: string | null = null) =>
  readWhatsWorking(await crm.read<unknown>("crm_whats_working", { p_campaign: campaign }));

export const setLessonStatus = (crm: Crm, lesson: string, status: Exclude<LessonStatus, "candidate">) =>
  crm.write<unknown>("set_lesson_status", { lesson, status });

/** "Make this a rule": for one campaign, or every campaign when campaign is null. */
export const addInstruction = (crm: Crm, statement: string, campaign: string | null) =>
  crm.write<string>("add_instruction", { statement, ...(campaign ? { campaign } : {}) });

export async function parseBrief(crm: Crm, text: string): Promise<{ parsed: boolean; chips: BriefChips }> {
  const r = obj(await crm.write<unknown>("parse_brief", { text }));
  return { parsed: r.parsed === true, chips: readChips(r.chips) };
}

export async function setCampaignBrief(crm: Crm, campaign: string, brief: CampaignBrief | null): Promise<CampaignBrief | null> {
  const r = obj(await crm.write<unknown>("set_campaign_brief", { campaign, brief: brief ? { text: brief.text, chips: brief.chips } : { text: "" } }));
  return readBrief(r.brief);
}

/** The weekly digest's payload (task kind learning_digest). */
export function readDigest(payload: unknown): { week: string | null; lessons: { id: string; statement: string; kind: string; confidence: Confidence | null }[] } {
  const o = obj(payload);
  return {
    week: str(o.week),
    lessons: (Array.isArray(o.lessons) ? o.lessons : []).map(obj).filter((l) => str(l.id) && str(l.statement)).map((l) => ({
      id: String(l.id), statement: String(l.statement), kind: String(l.kind ?? "performance"), confidence: CONFIDENCES.find((c) => c === l.confidence) ?? null,
    })),
  };
}
